import React, { createContext, useContext, useEffect, useMemo } from 'react';

import useConsolidatedInventorySources from '../settings/inventory-sources/use-consolidated-inventory-sources';

const CONNECTION_TYPE_SHIPENGINE = 'shipengine';
const EXTERNAL_INVENTORY_SOURCE_TYPE = 'source';
const ACTIVE_STATUS = 'active';

const ExternalInventoryConnectionsContext = createContext({
  connectionSources: [],
  connectionIds: [],
});
export const useExternalInventoryConnectionsContext = () => {
  return useContext(ExternalInventoryConnectionsContext);
};

/**
 * Provider component for external inventory connections.
 * Fetches and provides filtered external inventory connection data to child components.
 * Filters connections to only include active external inventory sources that are not ShipEngine connections.
 * @param {object} props - Component props
 * @param {React.ReactNode} [props.children] - Child components to render
 * @returns {React.ReactElement} The provider component
 */
export const ExternalInventoryConnectionsProvider = ({ children = null }) => {
  const { connections, fetchConsolidatedInventoryConnections, loading: connectionsLoading } = useConsolidatedInventorySources();

  useEffect(() => {
    fetchConsolidatedInventoryConnections();
  }, [fetchConsolidatedInventoryConnections]);

  const value = useMemo(() => {
    const connectionSources =
      connections?.filter(
        (connection) =>
          connection?.connectionType?.toLowerCase() !== CONNECTION_TYPE_SHIPENGINE &&
          connection?.terminusType?.toLowerCase() === EXTERNAL_INVENTORY_SOURCE_TYPE &&
          connection?.status?.toLowerCase() === ACTIVE_STATUS,
      ) ?? [];

    const hasEnabledConnections = connectionsLoading ? true : connectionSources.length > 0;
    const connectionIds = connectionSources.map((connection) => connection.connectionId);

    return { connectionSources, connectionIds, hasEnabledConnections };
  }, [connections, connectionsLoading]);

  return <ExternalInventoryConnectionsContext.Provider value={value}>{children}</ExternalInventoryConnectionsContext.Provider>;
};
